import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SignatureCanvas from "react-signature-canvas";
import "./create-signature-popup.scss";
import SelectInputComponent from "../select-input-component/select-input-component";
import { Button } from "reactstrap";
import AlertComponent from "../alert-component/alert-component";

interface SignaturePopupProps {
  open: boolean;
  onClose: () => void;
}

const SignaturePopup: React.FC<SignaturePopupProps> = ({ open, onClose }) => {
  const [format, setFormat] = useState<string>(
    "Select Signature Image or Other Options"
  );
  const [signatureData, setSignatureData] = useState<string | null>(null);
  const signatureFormats = [
    { id: 1, value: "Signature Image", label: "Signature Image" },
    { id: 2, value: "svg", label: "svg" },
    { id: 3, value: "svgbase64", label: "svgbase64" },
    { id: 4, value: "image/svg+xml", label: "image/svg+xml" },
    { id: 5, value: "image/svg+xml;base64", label: "image/svg+xml;base64" },
  ];
  const [hasDrawn, setHasDrawn] = useState(false);
  const [alertType, setAlertType] = useState<"success" | "error">('success');
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertOpen, setAlertOpen] = useState(false);

  const sigCanvas = useRef<any>(null);

  const handleUndo = () => {
    if (sigCanvas.current) {
      const signaturePad = sigCanvas.current.getSignaturePad();
      const data = signaturePad.toData();
      if (data?.length > 0) {
        data.pop(); // remove last stroke
        signaturePad.fromData(data);
      }
      // If nothing left after undo, hide the button
      if (data?.length === 0) {
        setHasDrawn(false);
      }
    }
  };

  const handleClear = () => {
    if (sigCanvas.current) {
      sigCanvas.current?.clear();
      setHasDrawn(false);
    }
  };

  const generateImageWithGivenFormat = () => {
    if (!sigCanvas?.current) return;
    // if (format === "base64") {
    //   // PNG with trimming
    //   data = sigCanvas?.current?.getTrimmedCanvas().toDataURL();
    // } else {
    //   // fallback base64 (default PNG data URL)
    //   data = sigCanvas?.current?.getTrimmedCanvas().toDataURL(format);
    // }
    const data = sigCanvas?.current?.getTrimmedCanvas().toDataURL("image/png");
    setSignatureData(data);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const fileType = file.type.toLowerCase();
      if (!fileType.startsWith("image/")) {
        setAlertType("error");
        setAlertMessage("Invalid file type. Please upload an image.");
        setAlertOpen(true);
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setSignatureData(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  useEffect(() => {
    if (open) {
      handleClear();
      setSignatureData(null);
      setFormat("Select Signature Image or Other Options");
    }
  }, [open]);

  return (
    <div className="signature-wrapper">
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        className="signature-wrapper-popup"
      >
        <DialogTitle className="dialog-title">
          Signature
          <IconButton
            aria-label="close"
            onClick={onClose}
            className="close-btn"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {/* Signature Area */}
          <div className="signature-canvas" style={{ position: "relative" }}>
            {/* Overlay label on border */}
            {!hasDrawn && (
              <div className="overlay-sign-here-label">
                Sign Here
                {/* Arrow pointing down to the border */}
                <div className="overlay-sign-here-arrow" />
              </div>
            )}
            <SignatureCanvas
              ref={sigCanvas}
              penColor="#00008b"
              canvasProps={{
                className: "sigCanvas",
              }}
              onEnd={() => setHasDrawn(true)}
            />
            {/* Undo last stroke means where we stopped our cursor */}
            {hasDrawn && (
              <button
                type="button"
                onClick={handleUndo}
                className="undo-button"
              >
                Undo
              </button>
            )}
          </div>

          {/* Format Dropdown + Buttons */}
          <div className="controls">
            <span className="section-title">Extract signature data as:</span>
            {/* <SelectInputComponent
              fieldName="Signature-formats"
              value={format}
              onChange={(e: any) => {
               
              }}
              options={signatureFormats}
              handleOption={(option) => {
                console.log("Option selected:", option);
                setFormat(option.value);
                generateImageWithGivenFormat(option.value);
              }}
            /> */}
            <Button
              type="button"
              className="generate-button update-btn"
              onClick={generateImageWithGivenFormat}
              disabled={!hasDrawn}
            >
              Generate Signature Image
            </Button>
            <span className="section-title-or">or:</span>
            <>
              <input
                type="file"
                accept="image/*"
                id="upload-input"
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
              <label htmlFor="upload-input" className="upload-signature-label update-btn">
                Upload Signature Image
              </label>
            </>
            <Button
              type="button"
              className="cancel-btn"
              onClick={handleClear}
              disabled={!hasDrawn}
            >
              Reset
            </Button>
          </div>

          {/* Preview */}
          {signatureData && (
            <div className="preview">
              <Typography variant="subtitle1" className="preview-title">
                Here is your signature:
              </Typography>
              <Typography variant="body2" className="preview-instruction">
                Right click on the image, click Copy Image and then right again
                in your template and click Paste.
              </Typography>
              <img
                src={signatureData}
                alt="Signature Preview"
                className="preview-img"
                key={new Date().getTime()} // Force re-render on new image
              />
              {/* {format !== "Signature Image" && (
                <Typography variant="body2"  sx={{ mt: 2 }}>
                  Your browser may not support this format for direct display.
                  Please use <strong>Signature Image</strong> for best compatibility,
                  or try a modern browser / open the exported file in an SVG viewer.
                </Typography>
              )} */}
            </div>
          )}
        </DialogContent>
        <AlertComponent
          open={alertOpen}
          onClose={() => setAlertOpen(false)}
          message={alertMessage}
          type={alertType}
        />
      </Dialog>
    </div>
  );
};

export default SignaturePopup;
